import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Button } from '@/components/ui/button';
import type {
  RecordListNavigationModel,
  RecordListTarget,
} from './list-record-navigation';

/**
 * Previous / next controls for one open record in a filtered list.
 * Icon buttons plus the position, matching the suggestion-detail sibling nav.
 * With `keyboardShortcuts`, K and J step back and forward (ignored while
 * typing in a field).
 */
export function ListRecordNavigation({
  subject,
  model,
  onOpen,
  keyboardShortcuts = false,
}: {
  subject: 'invoice' | 'task';
  model: RecordListNavigationModel;
  onOpen: (target: RecordListTarget) => void;
  keyboardShortcuts?: boolean;
}) {
  const gt = useGT();
  useHotkeys(
    'k',
    () => {
      if (model.previous) onOpen(model.previous);
    },
    { enabled: keyboardShortcuts },
    [model.previous, onOpen],
  );
  useHotkeys(
    'j',
    () => {
      if (model.next) onOpen(model.next);
    },
    { enabled: keyboardShortcuts },
    [model.next, onOpen],
  );
  const previousLabel =
    subject === 'invoice'
      ? gt('Previous invoice', {
          context: 'invoice detail: go to the previous invoice in the list',
        })
      : gt('Previous task', {
          context: 'task detail: go to the previous task in the list',
        });
  const nextLabel =
    subject === 'invoice'
      ? gt('Next invoice', {
          context: 'invoice detail: go to the next invoice in the list',
        })
      : gt('Next task', {
          context: 'task detail: go to the next task in the list',
        });
  const landmark =
    subject === 'invoice'
      ? gt('Invoices in this list', {
          context: 'landmark for previous and next invoice controls',
        })
      : gt('Tasks in this list', {
          context: 'landmark for previous and next task controls',
        });
  const position = gt('{current} / {total}', {
    context:
      'position of the open record in the filtered list, current and total counts',
    current: model.position.toLocaleString(),
    total: model.total.toLocaleString(),
  });

  return (
    <nav aria-label={landmark} className="flex shrink-0 items-center gap-1">
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={previousLabel}
        aria-keyshortcuts={keyboardShortcuts ? 'K' : undefined}
        title={keyboardShortcuts ? `${previousLabel} (K)` : previousLabel}
        disabled={model.previous === null}
        onClick={() => {
          if (model.previous) onOpen(model.previous);
        }}
      >
        <CaretLeftIcon className="size-4" />
      </Button>
      <span
        className="min-w-12 text-center text-xs tabular-nums whitespace-nowrap text-muted-foreground"
        aria-live="polite"
        aria-busy={model.pending}
      >
        {position}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={nextLabel}
        aria-keyshortcuts={keyboardShortcuts ? 'J' : undefined}
        title={keyboardShortcuts ? `${nextLabel} (J)` : nextLabel}
        disabled={model.next === null}
        onClick={() => {
          if (model.next) onOpen(model.next);
        }}
      >
        <CaretRightIcon className="size-4" />
      </Button>
    </nav>
  );
}
